import React from 'react';
import { SUB_SIGNAL_META } from '../data/marketData';
import CoverageBadge from './CoverageBadge';
import {
  TrendingUp, BarChart3, Landmark, Shield,
  Activity, Droplets, ArrowRightLeft, Globe, Info
} from 'lucide-react';

const iconMap = {
  TrendingUp, BarChart3, Landmark, Shield,
  Activity, Droplets, ArrowRightLeft, Globe,
};

function getScoreColor(score) {
  if (score <= 24) return '#22c55e';
  if (score <= 49) return '#eab308';
  if (score <= 74) return '#f97316';
  return '#ef4444';
}

function getScoreLabel(score) {
  if (score <= 24) return 'Clear';
  if (score <= 49) return 'Caution';
  if (score <= 74) return 'Elevated';
  return 'Danger';
}

function getOperationalLabel(score) {
  if (Number.isFinite(Number(score))) return 'Signal active';
  return 'Awaiting feed';
}

export default function SubSignalGrid({ subSignals = {}, currentDrop = 6, coverage = null }) {
  const entries = Object.entries(SUB_SIGNAL_META);
  const numericDrop = Number(currentDrop);
  const activeDrop = Number.isFinite(numericDrop) ? numericDrop : 6;

  const coverageByKey = (coverage?.components || []).reduce((acc, c) => {
    acc[c.key] = c.coverage;
    return acc;
  }, {});

  return (
    <div className="animate-slide-up" style={{ animationDelay: '0.2s' }}>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h3 className="font-display font-bold text-lg text-mentor-text">
          Signal Components
        </h3>
        {coverage ? (
          <div className="flex items-center gap-2 flex-wrap">
            {coverage.mode === 'live' ? (
              <span className="text-[11px] font-display font-bold text-mentor-text">
                {coverage.livePercent}% live data
              </span>
            ) : (
              <span className="text-[11px] font-display font-bold text-mentor-muted uppercase tracking-widest">
                Demo / training data
              </span>
            )}
            {coverage.mode === 'live' && (
              <span className="text-[10px] text-mentor-muted">
                {coverage.counts.live} live · {coverage.counts.estimated} estimated · {coverage.counts.backup} backup · {coverage.counts.placeholder} placeholder
              </span>
            )}
          </div>
        ) : (
          <span className="text-[10px] font-display uppercase tracking-widest text-mentor-muted">
            degraded feeds remain visible; live status is shown below
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {entries.map(([key, meta]) => {
          const rawScore = subSignals[key];
          const score = Number(rawScore);
          const hasScore = Number.isFinite(score);
          const isImplemented = meta.dropAvailable <= activeDrop;
          const IconComponent = iconMap[meta.icon] || Activity;
          const color = hasScore ? getScoreColor(score) : '#94a3b8';
          const softUnavailable = isImplemented && !hasScore;

          return (
            <div
              key={key}
              className={`stat-card relative ${softUnavailable ? 'border-dashed border-mentor-border/70 bg-mentor-card/55' : ''}`}
            >
              {!isImplemented && (
                <div className="absolute top-2.5 right-2.5 flex items-center gap-1 text-[10px] text-mentor-muted">
                  <Info size={12} /> Later
                </div>
              )}

              <div className="flex items-center justify-between mb-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${color}15`, border: `1px solid ${color}30` }}
                >
                  <IconComponent size={18} style={{ color }} />
                </div>
                {hasScore ? (
                  <span className="font-mono text-xl font-bold" style={{ color }}>
                    {Math.round(score)}
                  </span>
                ) : (
                  <span className="font-mono text-xs font-semibold text-mentor-muted">
                    Pending
                  </span>
                )}
              </div>

              <p className="font-display font-semibold text-sm text-mentor-text mb-1">
                {meta.label}
              </p>

              {hasScore ? (
                <div className="flex items-center gap-1.5 mt-1">
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-xs font-mono" style={{ color }}>
                    {getScoreLabel(score)}
                  </span>
                  <span className="text-xs text-mentor-muted ml-auto">
                    wt: {meta.weight}
                  </span>
                </div>
              ) : (
                <div className="mt-1">
                  <p className="text-xs text-mentor-muted">
                    {isImplemented ? 'Feed delayed — keeping component visible' : `Planned drop ${meta.dropAvailable}`}
                  </p>
                </div>
              )}

              <div className="mt-3 rounded-lg border border-mentor-border/40 bg-mentor-bg/25 px-2 py-1 flex items-center justify-between gap-2">
                <p className="text-[10px] font-display uppercase tracking-widest text-mentor-muted">
                  {getOperationalLabel(score)}
                </p>
                {coverageByKey[key] && (
                  <CoverageBadge coverage={coverageByKey[key]} size="xs" showDot={false} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
